import React, { useEffect, useState } from "react";

import classnames from "classnames";
import { useUpdateEmployeeFormMutation } from "EmployerApplication/api";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import isIntuit from "Intuit/isIntuit";
import { debounce } from "lodash";
import { isRenewal } from "QuotePage/urls";
import { Form, Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { OnChange } from "react-final-form-listeners";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { formatCurrencyAsFloat } from "Utils/Formatters";
import { validateRequired, validateZero, validateHundred, validateNumber, composeValidators, validateZipCode } from "Utils/Validations";

import Button from "Cobalt/Button";
import { TitleSmall } from "Cobalt/Header";
import { ModalBody, ModalFooter } from "Cobalt/Modal";
import { Select } from "Cobalt/Select";
import { Table, TH, TD, TR, ExpandingRow } from "Cobalt/Table";
import BodyText from "Common/BodyText";
import { replaceEmployeeProfile } from "Common/EnterAges/Actions";
import { formatFormValuesAsEmployeeProfile } from "Common/EnterAges/Components/Helpers/FormHelpers";
import { SIFinalFormField, SIFormInput } from "Common/forms";
import { zipCodeMask, currencyMask } from "Common/forms/masks";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import { SizeSwitch } from "Common/Responsive";
import SICheckbox from "Common/SICheckbox";

import makeBem from "Utils/makeBem";

const bem = makeBem("AddAncillaryPlanModal");
const focusOnErrors = createDecorator();

const validateAge = (age) => {
  if (age && age < 18) {
    return "Must be Older than 18";
  }

  if (age && age > 99) {
    return "Must be Younger than 100";
  }
  return undefined;
};

const validatorsWithAge = composeValidators(validateRequired, validateZero, validateHundred, validateNumber, validateAge);

const validatorsWithZipCode = composeValidators(validateRequired, validateZipCode);

const persistChangedSalariesDebounced = debounce(async (employees, updateEmployeeForm, storeSalaryById) => {
  const updates = [];
  for (const ee of employees || []) {
    const id = ee?.id;
    if (id == null) continue;

    const key = String(id);
    const salaryNum = Number.isFinite(ee.salary) ? ee.salary : null;
    const storeVal = storeSalaryById[key] ?? null;

    if ((salaryNum ?? null) !== (storeVal ?? null)) {
      updates.push({ id, salary: salaryNum });
    }
  }

  for (const update of updates) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await updateEmployeeForm({
        id: update.id,
        context: "basic_details",
        salary_frequency: "annually",
        salary_amount: update.salary,
      }).unwrap();
    } catch (err) {
      console.error("Failed to update salary", update.id, err);
    }
  }
}, 500);

// For renewal, we only want to disable fields for existing employees
const isAddedEmployee = (employee) => employee.name.includes("Employee ");

const AgeField = ({ name, employee }) => (
  <SIFinalFormField
    name={`${name}.age`}
    component={SIFormInput}
    validate={validatorsWithAge}
    componentProps={{
      type: "number",
      min: 0,
      max: 99,
      disabled: isRenewal() && !isAddedEmployee(employee),
    }}
    mb={0}
    pl={0}
    className={bem("ageInput")}
    hideErrorText
  />
);

const ZipCodeField = ({ name, employee }) => (
  <SIFinalFormField
    name={`${name}.zip_code`}
    component={SIFormInput}
    validate={validatorsWithZipCode}
    componentProps={{
      type: "string",
      mask: zipCodeMask,
      disabled: isRenewal() && !isAddedEmployee(employee),
    }}
    mb={0}
    pl={0}
    className={bem("zipCodeInput")}
    hideErrorText
  />
);

const SalaryField = ({ name }) => (
  <SIFinalFormField
    name={`${name}.salary`}
    component={SIFormInput}
    validate={validateRequired}
    componentProps={{
      mask: currencyMask,
    }}
    parse={formatCurrencyAsFloat}
    format={formatCurrencyAsFloat}
    mb={0}
    pl={0}
    className={bem("salaryInput")}
    hideErrorText
  />
);

const GenderDropdown = ({ name }) => (
  <SIFinalFormField
    name={`${name}.gender`}
    component={Select}
    validate={validateRequired}
    componentProps={{
      type: "string",
      options: [
        ["male", "Male"],
        ["female", "Female"],
      ],
    }}
    mb={0}
    pl={0}
    className={bem("genderInput")}
    hideErrorText
  />
);

const FullTimeStatusCheckbox = ({ name }) => (
  <Field name={`${name}.full_time`} type="checkbox">
    {({ input }) => (
      <div>
        <SICheckbox {...input} />
      </div>
    )}
  </Field>
);

const MobileTable = () => (
  <Table mt={4} className={bem("mobileTable")}>
    <thead className={bem("mobileHeader")}>
      <TR>
        <TH>Employees</TH>
        <TH />
      </TR>
    </thead>
    <tbody className={bem("table")}>
      <FieldArray name="employees">
        {({ fields }) =>
          fields.map((name, index) => {
            const employee = fields.value[index];
            return (
              <ExpandingRow
                key={index}
                className={bem()}
                colspan="4"
                mainContent={
                  <React.Fragment key={name}>
                    <TD>{employee.name}</TD>
                  </React.Fragment>
                }
                expandingContent={
                  <>
                    <TR>
                      <TD>Age</TD>
                      <TD>{AgeField({ name, employee })}</TD>
                    </TR>
                    <TR>
                      <TD>Gender</TD>
                      <TD>{GenderDropdown({ name })}</TD>
                    </TR>
                    <TR>
                      <TD>Salary</TD>
                      <TD>{SalaryField({ name })}</TD>
                    </TR>
                    <TR>
                      <TD>ZIP Code</TD>
                      <TD>{ZipCodeField({ name, employee })}</TD>
                    </TR>
                  </>
                }
              />
            );
          })
        }
      </FieldArray>
    </tbody>
  </Table>
);

const EditEmployeeInformationDesktopTable = () => (
  <Table mt={4}>
    <thead>
      <TR>
        <TH>Full time</TH>
        <TH>Employee</TH>
        <TH>Salary</TH>
        <TH>Gender</TH>
        <TH>Age</TH>
        <TH>Zip code</TH>
      </TR>
    </thead>
    <tbody className={bem("table")}>
      <FieldArray name="employees">
        {({ fields }) =>
          fields.map((name, index) => {
            const employee = fields.value[index];
            return (
              <React.Fragment key={index}>
                <TR key={name}>
                  <TD>{FullTimeStatusCheckbox({ name })}</TD>
                  <TD>{employee.name}</TD>
                  <TD>{SalaryField({ name })}</TD>
                  <TD>{GenderDropdown({ name })}</TD>
                  <TD>{AgeField({ name, employee })}</TD>
                  <TD>{ZipCodeField({ name, employee })}</TD>
                </TR>
              </React.Fragment>
            );
          })
        }
      </FieldArray>
    </tbody>
  </Table>
);

const delayedReplaceEmployeeProfile = debounce((dispatch, values) => {
  dispatch(replaceEmployeeProfile(formatFormValuesAsEmployeeProfile(values)));
}, 300);

const EditEmployeeInformationForm = ({ form, handleSubmit, dirty }) => {
  const dispatch = useDispatch();
  const employeeProfileFromStore = useSelector((state) => state.employeeProfile);
  const [updateEmployeeForm] = useUpdateEmployeeFormMutation();

  const storeSalaryById = Object.fromEntries((employeeProfileFromStore || []).filter((e) => e && e.id != null).map((e) => [String(e.id), e.salary ?? null]));

  return (
    <form onSubmit={handleSubmit}>
      <SizeSwitch xs={<MobileTable />} sm={<EditEmployeeInformationDesktopTable form={form} />} />

      <OnChange name="employees">
        {(values) => {
          if (dirty) {
            const updatedEmployeeProfile = values.map((employee) => ({
              ...employee,
              salary: formatCurrencyAsFloat(employee.salary),
              full_time: !!employee.full_time,
            }));

            delayedReplaceEmployeeProfile(dispatch, updatedEmployeeProfile);

            if (isRenewal()) {
              persistChangedSalariesDebounced(updatedEmployeeProfile, updateEmployeeForm, storeSalaryById);
            }
          }
        }}
      </OnChange>
    </form>
  );
};

const EmployeeTable = ({ formattedEmployeeProfile }) => {
  const initialValues = {
    employees: formattedEmployeeProfile,
  };

  return (
    <Form onSubmit={() => {}} decorators={[focusOnErrors]} initialValues={initialValues} mutators={{ ...arrayMutators }}>
      {(props) => <EditEmployeeInformationForm {...props} />}
    </Form>
  );
};

const isCensusComplete = (employees) => {
  const fullTimeEmployees = employees?.filter((employee) => employee.full_time);
  return fullTimeEmployees?.every((employee) => !!employee.salary && !!employee.gender && !!employee.zip_code && !!employee.age);
};

export const SalaryPage = ({ setCurrentPage }) => {
  const previousPage = "sicPage";
  const nextPage = "plansPage";
  const pageNumber = 3;
  const totalPages = 4;

  const dispatch = useDispatch();

  const employeeProfile = useSelector((state) => state.employeeProfile);
  const formattedEmployeeProfile = formatFormValuesAsEmployeeProfile(employeeProfile);

  useEffect(() => {
    const employeeProfileWithFullTimeDefaults = employeeProfile?.map((employee) =>
      employee.full_time === undefined ? { ...employee, full_time: true } : employee,
    );

    dispatch(replaceEmployeeProfile(employeeProfileWithFullTimeDefaults));
  }, []);

  const [incompleteCensus, setIncompleteCensus] = useState(!isCensusComplete(formattedEmployeeProfile));

  useEffect(() => {
    if (isCensusComplete(formattedEmployeeProfile)) {
      AnalyticsTracking.trackEvent("disability_all_census_fields_completed");
    }
    setIncompleteCensus(!isCensusComplete(formattedEmployeeProfile));
  }, [employeeProfile]);

  const skipToEstimatedRates = (nextPage) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      label: "disability_skip_to_estimated_rates",
    });

    setCurrentPage(nextPage);
  };

  return (
    <>
      <ModalBody className={isIntuit() ? classnames(bem("body-intuit"), bem("sicLifePage")) : classnames(bem("body"), bem("sicLifePage"))}>
        <Row>
          <Col>
            <TitleSmall mt={0} mb={2}>
              Insurance carriers require this information in order to provide a quote.
            </TitleSmall>
          </Col>
        </Row>
        <EmployeeTable formattedEmployeeProfile={formattedEmployeeProfile} setIncompleteCensus={setIncompleteCensus} />
      </ModalBody>
      <ModalFooter className="text-left">
        <SizeSwitch
          sm={
            <Row className={bem("numberedFooter")}>
              {previousPage ? (
                <Button color="tertiary" className="pull-left" onClick={() => setCurrentPage(previousPage)}>
                  <Icon icon="chevron-left" pr={3} />
                  Back
                </Button>
              ) : null}
              <BodyText className={bem("modalPageNumber")} size="small" pl={0}>
                Step {pageNumber} / {totalPages}
              </BodyText>
              <Button color="secondary" className="float-right" onClick={() => skipToEstimatedRates(nextPage)} mr={4} disabled={!incompleteCensus}>
                Skip to estimated rates
              </Button>
              <Button color="primary" className="float-right" onClick={() => setCurrentPage(nextPage)} disabled={incompleteCensus}>
                Continue
              </Button>
            </Row>
          }
          xs={
            <div className={bem("mobileFooter")}>
              <Button color="white" fullWidth onClick={() => setCurrentPage(previousPage)}>
                Back
              </Button>
              <Button color="secondary" fullWidth onClick={() => skipToEstimatedRates(nextPage)} disabled={!incompleteCensus}>
                Skip to estimated rates
              </Button>
              <Button color="primary" fullWidth onClick={() => setCurrentPage(nextPage)} disabled={incompleteCensus}>
                Continue
              </Button>
            </div>
          }
        />
      </ModalFooter>
    </>
  );
};
